import { useState } from 'react';

import { message } from 'antd';
import { AxiosError } from 'axios';

import http, { isAuthError } from '@/api/utils/http.ts';
import { firebaseAuth } from '@/firestore/config.ts';

interface UseServerFileDownloadResult {
    downloadFile: (filePath: string, filename?: string) => Promise<Blob>;
    downloading: boolean;
    downloadError: Error | null;
}

/**
 * Hook for downloading files from Firebase Storage through the server API
 * Replaces direct Firebase storage downloads with server-proxied downloads
 */
export const useServerFileDownload = (): UseServerFileDownloadResult => {
    const [downloading, setDownloading] = useState<boolean>(false);
    const [downloadError, setDownloadError] = useState<Error | null>(null);

    const downloadFile = async (filePath: string, filename?: string): Promise<Blob> => {
        try {
            setDownloading(true);
            setDownloadError(null);

            // Get auth token for the request
            const token = await firebaseAuth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('User not authenticated');
            }

            if (!http || !http.get) {
                throw new Error('HTTP client not available');
            }

            const response = await http.get('/config/storage/file', {
                params: { filePath },
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob',
            });

            const blob = response.data;
      
            // If filename is provided, trigger a download
            if (filename) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }

            return blob;
        } catch (err) {
            // ENV: Log error
            console.error('Error downloading file:', err);
            
            // 401 errors are already handled in http service with complete Firebase and Auth0 logout
            if (!isAuthError(err as AxiosError)) {
                setDownloadError(err instanceof Error ? err : new Error('Unknown download error'));
                message.error('Failed to download file: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
            
            throw err;
        } finally {
            setDownloading(false);
        }
    };

    return {
        downloadFile,
        downloading,
        downloadError,
    };
}; 
